import { useEffect, useState } from "react";

import dayjs from "dayjs";

import {
  fetchCommunityOptions,
  fetchGarretOptions,
  fetchLeafOptions,
} from "@/apis/organization";
import {
  putUserState,
  updateUserInfo,
  updateUserPermissions,
} from "@/apis/user";
import AlertModal from "@/components/Modal/AlertModal";
import ConfirmModal from "@/components/Modal/ConfirmModal";
import useModal from "@/hooks/useModal";
import { UserByState } from "@/types/user";

const PERMISSION_OPTIONS = [
  { key: "parking", label: "주차팀" },
  { key: "youth_admin", label: "대학청년부 관리" },
];

interface UserDetailsProps {
  user: UserByState;
  onUpdated?: () => void;
}

const InfoRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-baseline border-b border-gray-100 py-3 text-sm last:border-b-0">
    <div className="w-20 shrink-0 font-medium text-gray-500">{label}</div>
    <div className="flex-1 text-gray-800">{children}</div>
  </div>
);

const UserDetails = ({ user, onUpdated }: UserDetailsProps) => {
  const {
    createdDate,
    userName,
    telNo,
    cmtCd,
    cmtNm,
    garCd,
    garNm,
    leafCd,
    leafNm,
    userId,
    email,
    roleId,
    permissions,
  } = user;

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(userName);
  const [phone, setPhone] = useState(telNo);
  const [selectedCmt, setSelectedCmt] = useState({ id: cmtCd, name: cmtNm });
  const [selectedGar, setSelectedGar] = useState({ id: garCd, name: garNm });
  const [selectedLeaf, setSelectedLeaf] = useState({
    id: leafCd,
    name: leafNm,
  });

  const [communityList, setCommunityList] = useState<
    { id: string; name: string }[]
  >([]);
  const [garretList, setGarretList] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [leafList, setLeafList] = useState<{ id: string; name: string }[]>([]);
  const [saving, setSaving] = useState(false);

  // 권한 관련
  const initialPerms = (permissions || "")
    .split(",")
    .map(s => s.trim())
    .filter(Boolean);
  const [pendingPerms, setPendingPerms] = useState<string[]>(initialPerms);
  const [permSaving, setPermSaving] = useState(false);
  const permAlert = useModal();
  const [permAlertMsg, setPermAlertMsg] = useState("");

  const permChanged =
    JSON.stringify([...pendingPerms].sort()) !==
    JSON.stringify([...initialPerms].sort());

  // 상태 변경 관련
  const confirmDeleteModal = useModal();
  const confirmRevertModal = useModal();
  const stateAlert = useModal();
  const [stateLoading, setStateLoading] = useState(false);
  const [stateAlertMsg, setStateAlertMsg] = useState("");
  const [stateActionSuccess, setStateActionSuccess] = useState(false);

  const handleDeleteUser = async () => {
    if (stateLoading) return;
    setStateLoading(true);
    try {
      await putUserState({ userId, userStateCode: "09" });
      setStateAlertMsg("회원이 삭제 처리되었습니다.");
      setStateActionSuccess(true);
    } catch {
      setStateAlertMsg("회원 삭제에 실패했습니다. 다시 시도해 주세요.");
      setStateActionSuccess(false);
    } finally {
      setStateLoading(false);
      confirmDeleteModal.onClose();
      stateAlert.onOpen();
    }
  };

  const handleRevertToPending = async () => {
    if (stateLoading) return;
    setStateLoading(true);
    try {
      await putUserState({ userId, userStateCode: "00" });
      setStateAlertMsg("해당 회원이 승인요청 상태로 되돌려졌습니다.");
      setStateActionSuccess(true);
    } catch {
      setStateAlertMsg("상태 변경에 실패했습니다. 다시 시도해 주세요.");
      setStateActionSuccess(false);
    } finally {
      setStateLoading(false);
      confirmRevertModal.onClose();
      stateAlert.onOpen();
    }
  };

  const handleStateAlertClose = () => {
    stateAlert.onClose();
    if (stateActionSuccess) {
      onUpdated?.();
    }
  };

  const handlePermToggle = (perm: string) => {
    setPendingPerms(prev =>
      prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm],
    );
  };

  const handlePermSave = async () => {
    setPermSaving(true);
    try {
      await updateUserPermissions(userId, pendingPerms.join(","));
      user.permissions = pendingPerms.join(",");
      setPermAlertMsg("권한이 변경되었습니다.");
    } catch {
      setPendingPerms(initialPerms);
      setPermAlertMsg("권한 변경에 실패했습니다.");
    } finally {
      setPermSaving(false);
      permAlert.onOpen();
    }
  };

  useEffect(() => {
    if (editing) {
      loadCommunities();
      if (selectedCmt.id) loadGarrets(selectedCmt.id);
      if (selectedCmt.id && selectedGar.id)
        loadLeaves(selectedCmt.id, selectedGar.id);
    }
  }, [editing]);

  const loadCommunities = async () => {
    setCommunityList(await fetchCommunityOptions());
  };

  const loadGarrets = async (cmtCd: string) => {
    setGarretList(await fetchGarretOptions(cmtCd));
  };

  const loadLeaves = async (cmtCd: string, garCd: string) => {
    setLeafList(await fetchLeafOptions(cmtCd, garCd));
  };

  const handleCmtChange = (cmtId: string) => {
    const cmt = communityList.find(c => c.id === cmtId);
    setSelectedCmt(cmt || { id: cmtId, name: "" });
    setSelectedGar({ id: "", name: "" });
    setSelectedLeaf({ id: "", name: "" });
    setGarretList([]);
    setLeafList([]);
    if (cmtId) loadGarrets(cmtId);
  };

  const handleGarChange = (garId: string) => {
    const gar = garretList.find(g => g.id === garId);
    setSelectedGar(gar || { id: garId, name: "" });
    setSelectedLeaf({ id: "", name: "" });
    setLeafList([]);
    if (selectedCmt.id && garId) loadLeaves(selectedCmt.id, garId);
  };

  const handleLeafChange = (leafId: string) => {
    const leaf = leafList.find(l => l.id === leafId);
    setSelectedLeaf(leaf || { id: leafId, name: "" });
  };

  const handlePhoneChange = (value: string) => {
    const raw = value.replace(/[^0-9]/g, "").slice(0, 11);
    let formatted = raw;
    if (raw.length > 7) {
      formatted = `${raw.slice(0, 3)}-${raw.slice(3, 7)}-${raw.slice(7)}`;
    } else if (raw.length > 3) {
      formatted = `${raw.slice(0, 3)}-${raw.slice(3)}`;
    }
    setPhone(formatted);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUserInfo(userId, {
        userName: name,
        email: email || "123@kakao.com",
        telNo: phone,
        cpsCd: "PTK",
        cmtCd: selectedCmt.id,
        garCd: selectedGar.id,
        leafCd: selectedLeaf.id,
        roleId,
      });
      setEditing(false);
      onUpdated?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setName(userName);
    setPhone(telNo);
    setSelectedCmt({ id: cmtCd, name: cmtNm });
    setSelectedGar({ id: garCd, name: garNm });
    setSelectedLeaf({ id: leafCd, name: leafNm });
    setEditing(false);
  };

  const getOrganization = () => {
    const parts = [cmtNm, garNm, leafNm].filter(Boolean);

    return parts.join(" > ") || "-";
  };

  const selectClass =
    "w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary";
  const inputClass =
    "w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary";

  if (!editing) {
    return (
      <>
        <div className="flex flex-col">
          <InfoRow label="가입일자">
            {dayjs(createdDate)
              .locale("ko")
              .format("YYYY년 MM월 DD일 (ddd요일)")}
          </InfoRow>
          <InfoRow label="이름">{userName || "-"}</InfoRow>
          <InfoRow label="연락처">{telNo || "-"}</InfoRow>
          <InfoRow label="소속">{getOrganization()}</InfoRow>
          <InfoRow label="팀 권한">
            <div className="flex items-center gap-2">
              {PERMISSION_OPTIONS.map(({ key, label }) => {
                const active = pendingPerms.includes(key);

                return (
                  <label
                    key={key}
                    className="flex cursor-pointer select-none items-center gap-1.5"
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => handlePermToggle(key)}
                      className="h-4 w-4 rounded border-gray-300 text-primary accent-primary"
                    />
                    <span className="text-xs text-gray-700">{label}</span>
                  </label>
                );
              })}
              {permChanged && (
                <button
                  type="button"
                  onClick={handlePermSave}
                  disabled={permSaving}
                  className="ml-2 rounded-md bg-primary px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
                >
                  {permSaving ? "저장 중..." : "권한 저장"}
                </button>
              )}
            </div>
          </InfoRow>
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={confirmRevertModal.onOpen}
              disabled={stateLoading}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              승인요청으로 되돌리기
            </button>
            <button
              type="button"
              onClick={confirmDeleteModal.onOpen}
              disabled={stateLoading}
              className="rounded-md border border-red-500 px-3 py-1.5 text-xs font-medium text-red-500 transition-colors hover:bg-red-500 hover:text-white disabled:opacity-50"
            >
              회원 삭제
            </button>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-md border border-primary px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary hover:text-white"
            >
              회원정보 수정
            </button>
          </div>
        </div>
        {permAlert.isOpen && (
          <AlertModal onClose={permAlert.onClose}>{permAlertMsg}</AlertModal>
        )}
        {stateAlert.isOpen && (
          <AlertModal onClose={handleStateAlertClose}>
            {stateAlertMsg}
          </AlertModal>
        )}
        {confirmDeleteModal.isOpen && (
          <ConfirmModal
            title="회원 삭제"
            onConfirm={handleDeleteUser}
            onClose={confirmDeleteModal.onClose}
          >
            {`${userName || "해당"} 회원을 삭제 처리하시겠습니까?`}
          </ConfirmModal>
        )}
        {confirmRevertModal.isOpen && (
          <ConfirmModal
            title="승인요청으로 되돌리기"
            onConfirm={handleRevertToPending}
            onClose={confirmRevertModal.onClose}
          >
            {`${userName || "해당"} 회원을 승인요청 상태로 되돌리시겠습니까?`}
          </ConfirmModal>
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col">
      <InfoRow label="이름">
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          className={inputClass}
        />
      </InfoRow>
      <InfoRow label="연락처">
        <input
          type="text"
          value={phone}
          onChange={e => handlePhoneChange(e.target.value)}
          inputMode="numeric"
          maxLength={13}
          className={inputClass}
        />
      </InfoRow>
      <InfoRow label="공동체">
        <select
          value={selectedCmt.id}
          onChange={e => handleCmtChange(e.target.value)}
          className={selectClass}
        >
          <option value="">선택</option>
          {communityList.map(c => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </InfoRow>
      <InfoRow label="다락방">
        <select
          value={selectedGar.id}
          onChange={e => handleGarChange(e.target.value)}
          disabled={!selectedCmt.id}
          className={selectClass}
        >
          <option value="">선택</option>
          {garretList.map(g => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      </InfoRow>
      <InfoRow label="순">
        <select
          value={selectedLeaf.id}
          onChange={e => handleLeafChange(e.target.value)}
          disabled={!selectedGar.id}
          className={selectClass}
        >
          <option value="">선택</option>
          {leafList.map(l => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      </InfoRow>
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={handleCancel}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-50"
        >
          취소
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {saving ? "저장 중..." : "저장"}
        </button>
      </div>
    </div>
  );
};

export default UserDetails;
